import type { CSSProperties, ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { Popover } from '@base-ui/react/popover'
import {
  Bell,
  BellOff,
  CheckCheck,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  Info,
  Inbox,
  TriangleAlert,
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { apiFetch } from '@/api/client'
import type { NotificationType } from '@/domain'
import { Skeleton } from '../ui/skeleton'
import { notificationTarget } from './nav-target'
import { ICON_BTN_STYLE, POPUP_STYLE } from './popup-style'

/**
 * Notifications bell + popover. Reads GET /api/v1/notifications (the table is
 * member-readable; rows are what the worker SHOULD mint — InsertNotification has
 * no backend caller yet, so this models the intended feed). Rows are actionable
 * buttons: clicking navigates to a target DERIVED client-side from envId/type
 * (there is no `link` column) and marks the row read (POST /notifications/{id}/
 * read). The unread badge carries a count and the highest-severity unread tone.
 */

interface ShellNotification {
  id: string
  type: NotificationType
  title: string
  message?: string
  createdAt?: string
  envId: string | null
  read: boolean
}

const TYPE_META: Record<NotificationType, { token: string; icon: ReactNode }> = {
  success: { token: 'var(--ok-500)', icon: <CircleCheck size={14} aria-hidden /> },
  warning: { token: 'var(--warn-500)', icon: <TriangleAlert size={14} aria-hidden /> },
  // Notification chrome uses brand blue rather than alarm red — the row icon
  // still communicates "error" via CircleAlert + copy, just not via red.
  error: { token: 'var(--brand-600)', icon: <CircleAlert size={14} aria-hidden /> },
  info: { token: 'var(--info-500)', icon: <Info size={14} aria-hidden /> },
}

/** Highest-severity-first ordering used to tint the bell badge. */
const SEVERITY_ORDER: readonly NotificationType[] = ['error', 'warning', 'success', 'info']

function normalizeType(raw: unknown): NotificationType {
  return raw === 'success' || raw === 'warning' || raw === 'error' ? raw : 'info'
}

function normalizeOne(raw: unknown): ShellNotification | null {
  if (!raw || typeof raw !== 'object') return null
  const rec = raw as Record<string, unknown>
  if (typeof rec.id !== 'string' && typeof rec.id !== 'number') return null
  return {
    id: String(rec.id),
    type: normalizeType(rec.type),
    title: typeof rec.title === 'string' ? rec.title : 'Notification',
    message: typeof rec.message === 'string' ? rec.message : undefined,
    createdAt: typeof rec.createdAt === 'string' ? rec.createdAt : undefined,
    envId: typeof rec.envId === 'string' ? rec.envId : null,
    read: rec.read === true,
  }
}

function normalize(data: unknown): ShellNotification[] {
  const list = Array.isArray(data)
    ? data
    : data && typeof data === 'object' && Array.isArray((data as { items?: unknown }).items)
      ? (data as { items: unknown[] }).items
      : []
  return list.map(normalizeOne).filter((n): n is ShellNotification => n !== null)
}

/** The most severe tone among unread rows — drives the badge color. */
function topUnreadTone(items: ShellNotification[]): NotificationType {
  const unread = new Set(items.filter((n) => !n.read).map((n) => n.type))
  return SEVERITY_ORDER.find((t) => unread.has(t)) ?? 'info'
}

function relativeTime(iso: string | undefined): string {
  if (!iso) return ''
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true })
  } catch {
    return ''
  }
}

function NotificationRow({
  item,
  onActivate,
}: {
  item: ShellNotification
  onActivate: (item: ShellNotification) => void
}) {
  const meta = TYPE_META[item.type]
  return (
    <li style={{ listStyle: 'none' }}>
      <button
        type="button"
        className="it-menu-item"
        onClick={() => onActivate(item)}
        style={{
          display: 'flex',
          gap: 10,
          width: '100%',
          textAlign: 'left',
          padding: '10px 12px',
          border: 0,
          borderRadius: 'var(--r-sm)',
          cursor: 'pointer',
          font: 'inherit',
          background: item.read
            ? 'transparent'
            : `color-mix(in srgb, ${meta.token} 6%, transparent)`,
        }}
      >
        <span
          aria-hidden
          style={{
            width: 26,
            height: 26,
            borderRadius: 'var(--r-pill)',
            background: `color-mix(in srgb, ${meta.token} 14%, transparent)`,
            color: meta.token,
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            marginTop: 1,
          }}
        >
          {meta.icon}
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            style={{
              display: 'block',
              fontSize: 13,
              fontWeight: item.read ? 500 : 600,
              color: item.read ? 'var(--text-soft)' : 'var(--text)',
            }}
          >
            {item.title}
          </span>
          {item.message && (
            <span
              style={{
                display: 'block',
                fontSize: 12,
                color: 'var(--text-mute)',
                marginTop: 1,
              }}
            >
              {item.message}
            </span>
          )}
          <span
            style={{ display: 'block', fontSize: 11, color: 'var(--text-faint)', marginTop: 2 }}
            title={item.createdAt}
          >
            {relativeTime(item.createdAt)}
          </span>
        </span>
        {!item.read && (
          <span
            aria-label="Unread"
            style={{
              width: 7,
              height: 7,
              borderRadius: 'var(--r-pill)',
              background: meta.token,
              flexShrink: 0,
              marginTop: 6,
            }}
          />
        )}
      </button>
    </li>
  )
}

function EmptyOrError({ failed }: { failed: boolean }) {
  const style: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    padding: 'var(--s-6)',
    color: 'var(--text-mute)',
    fontSize: 12,
    textAlign: 'center',
  }
  return (
    <div style={style} role={failed ? 'alert' : undefined}>
      {failed ? (
        <BellOff size={18} color="var(--text-faint)" aria-hidden />
      ) : (
        <Inbox size={18} color="var(--text-faint)" aria-hidden />
      )}
      {failed ? "Couldn't load notifications" : "You're all caught up"}
    </div>
  )
}

export function NotificationsBell() {
  const router = useRouter()
  const queryClient = useQueryClient()
  const { data, isPending, isError } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => apiFetch<unknown>({ url: '/api/v1/notifications' }),
    retry: false,
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
  const items = normalize(data)
  const unreadCount = items.filter((n) => !n.read).length
  const badgeTone = TYPE_META[topUnreadTone(items)].token

  const markRead = useMutation({
    mutationFn: (id: string) =>
      apiFetch<unknown>({ url: `/api/v1/notifications/${id}/read`, method: 'POST' }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  })
  const markAllRead = useMutation({
    mutationFn: () =>
      apiFetch<unknown>({ url: '/api/v1/notifications/read-all', method: 'POST' }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  })

  const activate = (item: ShellNotification) => {
    if (!item.read) markRead.mutate(item.id)
    router.history.push(notificationTarget(item.envId, item.type))
  }

  return (
    <Popover.Root>
      <Popover.Trigger
        className="it-icon-btn it-shell-btn"
        aria-label={
          unreadCount > 0 ? `Notifications — ${unreadCount} unread` : 'Notifications'
        }
        style={ICON_BTN_STYLE}
      >
        <Bell size={16} aria-hidden />
        {unreadCount > 0 && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: 1,
              right: 0,
              minWidth: 15,
              height: 15,
              padding: '0 3px',
              borderRadius: 'var(--r-pill)',
              background: badgeTone,
              color: 'var(--brand-50)',
              fontSize: 9.5,
              fontWeight: 700,
              fontFamily: 'var(--ff-mono)',
              fontVariantNumeric: 'tabular-nums',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 0 2px var(--bg-elev)',
            }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner side="bottom" align="end" sideOffset={6} style={{ zIndex: 60 }}>
          <Popover.Popup style={{ ...POPUP_STYLE, width: 340, padding: 0 }}>
            <header
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 12px',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <Popover.Title
                style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--text)' }}
              >
                Notifications
              </Popover.Title>
              <button
                type="button"
                className="it-icon-btn it-shell-btn"
                onClick={() => markAllRead.mutate()}
                disabled={unreadCount === 0 || markAllRead.isPending}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  width: 'auto',
                  height: 26,
                  padding: '0 8px',
                  borderRadius: 'var(--r-sm)',
                  border: 0,
                  background: 'transparent',
                  color: unreadCount === 0 ? 'var(--text-faint)' : 'var(--link)',
                  cursor: unreadCount === 0 ? 'default' : 'pointer',
                  font: 'inherit',
                  fontSize: 12,
                }}
              >
                <CheckCheck size={13} aria-hidden />
                Mark all read
              </button>
            </header>
            <div style={{ maxHeight: 380, overflowY: 'auto', padding: 4 }}>
              {isPending && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 12 }}>
                  <Skeleton height={36} radius="var(--r-sm)" />
                  <Skeleton height={36} radius="var(--r-sm)" />
                  <Skeleton height={36} radius="var(--r-sm)" />
                </div>
              )}
              {!isPending && (isError || items.length === 0) && (
                <EmptyOrError failed={isError} />
              )}
              {!isPending && !isError && items.length > 0 && (
                <ul style={{ margin: 0, padding: 0 }}>
                  {items.map((item) => (
                    <NotificationRow key={item.id} item={item} onActivate={activate} />
                  ))}
                </ul>
              )}
            </div>
            {!isPending && !isError && items.length > 0 && (
              <footer style={{ borderTop: '1px solid var(--border)', padding: 4 }}>
                <button
                  type="button"
                  className="it-menu-item"
                  onClick={() => router.history.push('/organization')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    width: '100%',
                    height: 32,
                    border: 0,
                    borderRadius: 'var(--r-sm)',
                    background: 'transparent',
                    color: 'var(--text-soft)',
                    cursor: 'pointer',
                    font: 'inherit',
                    fontSize: 12,
                  }}
                >
                  Org activity
                  <ChevronRight size={13} aria-hidden />
                </button>
              </footer>
            )}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  )
}
